// The step orchestrator (§18.1): one round tick in the fixed phase order, in server mode or in
// predict mode (§18.2: `ctx.predictSlot` is the local slot). In predict mode the other beans are
// puppets (no inputs, frozen timers, bounded dead-reckoning), and every server-authoritative
// branch is skipped by the module that owns it.
import { DT, INTRO_TICKS, MAX_BEANS, NO_SLOT, TUNE } from '../constants';
import { EV, emit } from '../events';
import { BS } from '../ids';
import { createRoundClock, roundClock } from '../maps/clock';
import { MAPS } from '../maps/index';
import { MODES } from '../modes/index';
import { setV, v2 } from '../math';
import type { Bean, InputFrame, StepContext, Vec2, World } from '../types';
import { tickFloat } from './balloon';
import { deriveBeanStats } from './bean';
import { syncCarry } from './carry';
import { resolveCollisions } from './collision';
import { beginActions, resolveHits, tickCombatState } from './combat';
import { edgeActions, stepEdges } from './edges';
import { ghostActions } from './ghosts';
import { applyDeadzone, createPressSet, decodePresses } from './input';
import { stepCrates, stepEntities, useItem } from './items';
import { record } from './lagcomp';
import { deadReckon, handleJump, moveBean, updateFacing } from './movement';
import { isPuppet, isScoringMode } from './world';
import { aimYanks, endYanks } from './yank';

const clock = createRoundClock();
const presses = createPressSet();
/** Deadzoned move input per slot for this tick (input phase → movement phase). */
const moves: Vec2[] = Array.from({ length: MAX_BEANS }, () => v2());

/** Phase 1: round phase from the clock, the countdown stamps and the unfreeze at tick 0. */
function updatePhase(w: World): void {
  if (clock.phase !== w.phase) {
    w.phase = clock.phase;
    emit(w, EV.PHASE, NO_SLOT, clock.phase);
  }
  if (!isScoringMode(w.cfg.modeId) || w.tick > 0 || w.tick < -INTRO_TICKS) return;
  const step = TUNE.clock.countdownStepTicks;
  if (w.tick % step === 0) emit(w, EV.COUNTDOWN, NO_SLOT, (-w.tick / step) | 0);
  if (w.tick !== 0) return;
  for (const b of w.beans) {
    if (b.active && b.state === BS.FROZEN) {
      b.state = BS.NORMAL;
      b.st = 0;
    }
  }
}

/** Phase 2: state time, cooldowns and timers, Big Shake expiry, the balloon timer and timed combat exits. */
function tickTimers(w: World, b: Bean): void {
  b.st++;
  if (b.cdShove > 0) b.cdShove--;
  if (b.cdGrab > 0) b.cdGrab--;
  if (b.cdLedge > 0) b.cdLedge--;
  if (b.cdPebble > 0) b.cdPebble--;
  if (b.cdCup > 0) b.cdCup--;
  if (b.cdVend > 0) b.cdVend--;
  if (b.potatoImmuneTicks > 0) b.potatoImmuneTicks--;
  if (b.jumpBufferTicks > 0) b.jumpBufferTicks--;
  if (b.coyoteTicks > 0) b.coyoteTicks--;
  if (b.shakeTicks > 0 && --b.shakeTicks === 0) emit(w, EV.SHAKE_END, b.slot);
  tickFloat(w, b);
  tickCombatState(w, b);
}

/** Phase 3: presses, then actions in §18.1 order, then honks and emotes (allowed in every state). */
function handleInputs(w: World, inputs: readonly (InputFrame | null)[]): void {
  for (const b of w.beans) {
    if (!b.active || isPuppet(w, b.slot)) continue;
    const frame = inputs[b.slot] ?? null;
    const p = decodePresses(b, frame, presses);
    const move = frame ? applyDeadzone(frame.mx, frame.my, moves[b.slot]) : setV(moves[b.slot], 0, 0);
    updateFacing(b, move);
    beginActions(w, b, p, frame);
    useItem(w, b, p.item, frame);
    ghostActions(w, b, p.shove);
    handleJump(w, b, p.jump);
    edgeActions(w, b, p, move);
    if (p.honk) emit(w, EV.HONK, b.slot, 0);
    if (p.emote !== 0) emit(w, EV.EMOTE, b.slot, p.emote);
  }
}

/** Phase 10: invulnerability and Dizzy decay (4/s after 2.5 s without a hit; ghosts keep theirs). */
function tickMisc(w: World, b: Bean): void {
  if (b.invulnTicks > 0) b.invulnTicks--;
  if (b.dizzy > 0 && !b.out && w.tick - b.lastHitTick >= TUNE.dizzy.decayDelayTicks) {
    b.dizzy = Math.max(0, b.dizzy - TUNE.dizzy.decayPerSec * DT);
  }
}

/**
 * Advances `w` by one tick: increments `w.tick` first, runs the ordered phases of §18.1 and
 * appends events to `w.events`. `inputs[slot] == null` means the neutral input with unchanged
 * press counters.
 */
export function stepWorld(w: World, inputs: (InputFrame | null)[], ctx: StepContext): void {
  w.predictSlot = ctx.predictSlot === NO_SLOT ? undefined : ctx.predictSlot;
  const predict = w.predictSlot !== undefined;
  w.tick++;
  roundClock(w.cfg.mapId, w.cfg.modeId, w.tick, w.cfg.capTickOverride, clock);

  updatePhase(w);
  for (const b of w.beans) {
    deriveBeanStats(b);
    if (b.active && !isPuppet(w, b.slot)) tickTimers(w, b);
  }
  handleInputs(w, inputs);
  aimYanks(w);
  for (const b of w.beans) {
    if (!b.active) continue;
    if (isPuppet(w, b.slot)) deadReckon(w, b);
    else moveBean(w, b, moves[b.slot]);
  }
  endYanks(w);
  syncCarry(w);
  MAPS[w.cfg.mapId].step(w, clock);
  syncCarry(w);
  stepEntities(w);
  resolveCollisions(w);
  syncCarry(w);
  resolveHits(w, ctx);
  syncCarry(w);
  stepEdges(w);
  syncCarry(w);
  for (const b of w.beans) if (b.active && !isPuppet(w, b.slot)) tickMisc(w, b);
  MODES[w.cfg.modeId].onTick(w, clock, predict);
  if (!predict) stepCrates(w);
  record(w);
  w.predictSlot = undefined;
}
